import '../components/customer-checklist-work.css';
import { companyHasPlanFeature } from '../components/plan-gate.tsx';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, savedTeamsApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { Alert, Button, Checkbox, Select, TextArea, TextInput } from '../components/ui.tsx';
import { useSession } from '../state/session.tsx';
import { useCommercialWork } from '../components/commercial-gate.tsx';
import { isSamePhoneNumber, normalizePhoneToE164 } from '@shared/phone.ts';
import { getDateLocale } from '../i18n/locale.ts';
import { CUSTOMER_SERVICE_SEGMENTS } from '@shared/constants/index.ts';
import type { Customer } from '@shared/types/index.ts';
import { usePlanFeature } from '../components/plan-gate.tsx';

// Older cached customer responses predate these fields. Keep them genuinely
// optional at this boundary even after the shared Customer type learns about
// them, so an absent value is never mistaken for an explicit opt-out.
type CustomerWithMarketingPreferences = Omit<Customer, 'smsMarketingEnabled' | 'emailMarketingEnabled'> & {
  smsMarketingEnabled?: boolean;
  emailMarketingEnabled?: boolean;
};

const MARKETING_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeMarketingEmail(value: string | null | undefined): string | null {
  const normalized = (value ?? '').trim().toLowerCase();
  return MARKETING_EMAIL_PATTERN.test(normalized) ? normalized : null;
}

interface CustomerFormProps {
  customer?: Customer;
  onSaved: (customer: Customer) => void;
  onCancel?: () => void;
}

const EMPTY = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  billingAddressLine1: '',
  billingAddressLine2: '',
  billingCity: '',
  billingState: '',
  billingPostalCode: '',
  internalNotes: '',
  smsOptIn: false,
  smsMarketingEnabled: undefined as boolean | undefined,
  emailMarketingEnabled: undefined as boolean | undefined,
  /** Days from issue to due — Net 30 is 30. Null = due on receipt. */
  invoiceTermsDays: null as number | null,
  invoicePoNumber: '',
  consolidatedBilling: false,
  wantsEarlierTimes: false,
  serviceSegment: 'residential' as 'residential' | 'commercial',
  defaultTeamId: null as string | null,
  teamFlexible: false,
};

/** The net terms the select offers. A stored value off this list still shows — see below. */
const NET_TERMS_CHOICES = [15, 30, 45, 60];

export default function CustomerForm({ customer, onSaved, onCancel }: CustomerFormProps) {
  const { t, i18n } = useTranslation();
  const offersEarlierVisits = usePlanFeature('earlier_visit_offers');
  const queryClient = useQueryClient();
  const { company, can } = useSession();
  const teamPreferences = companyHasPlanFeature(company, 'scheduling') && can('schedule:read');
  const savedTeams = useQuery({
    queryKey: ['saved-teams'],
    queryFn: () => savedTeamsApi.list(),
    enabled: teamPreferences,
  });
  const savedCustomer = customer as CustomerWithMarketingPreferences | undefined;
  const country = company?.countryCode ?? 'US';
  const persistedSmsConsentPhone =
    savedCustomer?.smsConsentPhone ??
    (savedCustomer?.smsConsentStatus === 'opted_in' ? normalizePhoneToE164(savedCustomer.phone ?? '', country) : null);
  const initialMarketingRef = useRef({
    smsMarketingEnabled: savedCustomer?.smsMarketingEnabled,
    emailMarketingEnabled: savedCustomer?.emailMarketingEnabled,
  });
  // Draft permission belongs to the destination that was on screen when it
  // was checked. These refs intentionally do not follow later field edits.
  const draftSmsConsentPhoneRef = useRef<string | null>(null);
  const smsMarketingPhoneRef = useRef(savedCustomer?.smsMarketingEnabled === true ? persistedSmsConsentPhone : null);
  const emailMarketingAddressRef = useRef(
    savedCustomer?.emailMarketingEnabled === true ? normalizeMarketingEmail(savedCustomer.email) : null,
  );
  const [form, setForm] = useState(() =>
    savedCustomer
      ? {
          firstName: savedCustomer.firstName ?? '',
          lastName: savedCustomer.lastName ?? '',
          companyName: savedCustomer.companyName ?? '',
          email: savedCustomer.email ?? '',
          phone: savedCustomer.phone ?? '',
          billingAddressLine1: savedCustomer.billingAddressLine1 ?? '',
          billingAddressLine2: savedCustomer.billingAddressLine2 ?? '',
          billingCity: savedCustomer.billingCity ?? '',
          billingState: savedCustomer.billingState ?? '',
          billingPostalCode: savedCustomer.billingPostalCode ?? '',
          internalNotes: savedCustomer.internalNotes ?? '',
          smsOptIn: savedCustomer.smsConsentStatus === 'opted_in',
          smsMarketingEnabled: savedCustomer.smsMarketingEnabled,
          emailMarketingEnabled: savedCustomer.emailMarketingEnabled,
          invoiceTermsDays: savedCustomer.invoiceTermsDays,
          invoicePoNumber: savedCustomer.invoicePoNumber ?? '',
          consolidatedBilling: savedCustomer.consolidatedBilling,
          wantsEarlierTimes: savedCustomer.wantsEarlierTimes,
          serviceSegment: savedCustomer.serviceSegment ?? 'residential',
          defaultTeamId: savedCustomer.defaultTeamId ?? null,
          teamFlexible: savedCustomer.teamFlexible ?? false,
        }
      : EMPTY,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [moreDetails, setMoreDetails] = useState(Boolean(customer));
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (Object.keys(fieldErrors).length === 0) return;
    const invalid = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]');
    invalid?.focus();
    invalid?.scrollIntoView?.({ block: 'center' });
  }, [fieldErrors]);

  const commercialWork = useCommercialWork();
  /**
   * Whether this form carries an answer to one of the commercial billing
   * questions.
   *
   * Read from the DRAFT, which is seeded from the saved customer — so a
   * Net-30 client shows the block exactly as they did before, and a value
   * typed under commercial mode keeps it open if the session refreshes to
   * residential mid-edit. Reading the saved record alone would hide the block
   * while `form` went on submitting what was typed into it, which is how
   * somebody switches a customer to consolidated billing from a screen that
   * has stopped showing the choice.
   *
   * Nothing appears as it is typed, because a hidden block cannot be typed
   * into: the only way in is to already have an answer.
   */
  const carriesBillingTerms =
    form.invoiceTermsDays !== null || form.invoicePoNumber.trim() !== '' || form.consolidatedBilling;

  /**
   * Whether to save the billing address as this customer's first property.
   *
   * Ticked to start with, because the address a cleaner bills is nearly always
   * the address they clean, and typing it twice is the alternative. Offered
   * only when adding: an existing customer's properties are a visible list on
   * their own page, and appending another copy of the billing address from a
   * form that shows none of them would be a duplicate nobody saw coming.
   */
  const [addBillingProperty, setAddBillingProperty] = useState(true);

  function set(key: keyof typeof form) {
    return (event: { target: { value: string } }) => setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  // Checked against what has been typed, not against what was saved: someone
  // correcting a landline to a mobile should see the box become available
  // before they save, not after.
  const typedNumber = normalizePhoneToE164(form.phone, country);
  const textable = typedNumber !== null;
  const normalizedEmail = normalizeMarketingEmail(form.email);
  const emailReachable = normalizedEmail !== null;
  // `smsConfigured` covers both the deployment's Twilio account and one this
  // company connected itself.
  const smsSendable = company?.smsConfigured;
  const smsOffered = Boolean(smsSendable) && Boolean(company?.smsRemindersEnabled);
  /**
   * Whether the carrier has told us this number cannot receive texts.
   *
   * Checked against the number as typed, not just the stored one: the answer
   * belongs to a line, so it stops applying the moment the field is edited —
   * and a warning that lingered on a corrected number would be worse than none.
   * A landline does not *block* the opt-in — the customer may be about to give
   * a different number, and the send path refuses it anyway — it just says so.
   */
  const landlineOnFile =
    customer?.phoneLineType === 'landline' && isSamePhoneNumber(customer.phone, form.phone, country);

  // The number the stored consent belongs to. Held in a ref because it is what
  // the *saved* record says, and must not move as the field is typed in.
  const consentedNumberRef = useRef(persistedSmsConsentPhone);
  const consentCoversTypedNumber = consentedNumberRef.current !== null && typedNumber === consentedNumberRef.current;

  /**
   * Keeps the box in step with whichever number is currently typed.
   *
   * Consent belongs to a line, not to a person. Leaving the box ticked while
   * the number underneath it changes would submit `smsOptIn: true` for a
   * number nobody agreed on — and the API would faithfully record fresh
   * consent and text it.
   *
   * It restores as well as clears, which matters more than it sounds: a number
   * passes through unparseable states while it is being edited, so clearing on
   * the way out without restoring on the way back would leave the box unticked
   * for a customer whose number ended up exactly as it started — and that
   * unticked box submits as a withdrawal of a consent nobody withdrew.
   *
   * A deliberate untick survives, because this only runs when the typed number
   * changes.
   */
  useEffect(() => {
    const draftConsented = draftSmsConsentPhoneRef.current;
    if (draftConsented && typedNumber !== draftConsented) {
      draftSmsConsentPhoneRef.current = null;
      setForm((current) => (current.smsOptIn ? { ...current, smsOptIn: false } : current));
      return;
    }

    const consented = consentedNumberRef.current;
    if (!consented) return;

    const covered = typedNumber === consented && customer?.smsConsentStatus === 'opted_in';
    setForm((current) => (current.smsOptIn === covered ? current : { ...current, smsOptIn: covered }));
  }, [typedNumber, customer?.smsConsentStatus]);

  // A marketing choice is destination-specific too. A different valid email
  // or phone is not permission to contact the replacement; require the staff
  // member to record the customer's choice again.
  useEffect(() => {
    const consentedPhone = smsMarketingPhoneRef.current;
    if (consentedPhone && typedNumber !== consentedPhone) {
      smsMarketingPhoneRef.current = null;
      setForm((current) =>
        current.smsMarketingEnabled === true ? { ...current, smsMarketingEnabled: false } : current,
      );
    }

    const consentedEmail = emailMarketingAddressRef.current;
    if (consentedEmail && normalizedEmail !== consentedEmail) {
      emailMarketingAddressRef.current = null;
      setForm((current) =>
        current.emailMarketingEnabled === true ? { ...current, emailMarketingEnabled: false } : current,
      );
    }
  }, [typedNumber, normalizedEmail]);

  // Marketing permission is narrower than visit-reminder consent. It cannot
  // remain selected after its address disappears, and promotional texting
  // also requires the number-bound SMS agreement above. This only clears;
  // neither permission is ever inferred or pre-checked.
  useEffect(() => {
    setForm((current) => {
      const smsMarketingEnabled =
        current.smsMarketingEnabled === true && (!textable || !current.smsOptIn) ? false : current.smsMarketingEnabled;
      const emailMarketingEnabled =
        current.emailMarketingEnabled === true && !emailReachable ? false : current.emailMarketingEnabled;
      return smsMarketingEnabled === current.smsMarketingEnabled &&
        emailMarketingEnabled === current.emailMarketingEnabled
        ? current
        : { ...current, smsMarketingEnabled, emailMarketingEnabled };
    });
  }, [textable, emailReachable, form.smsOptIn, form.smsMarketingEnabled, form.emailMarketingEnabled]);

  /**
   * The payload, with `smsOptIn` included only when this form actually carries
   * an answer about the number being saved.
   *
   * Sending it unconditionally would mean an ordinary edit — a corrected
   * address, a note — silently recording an opt-out for a customer who had
   * agreed, simply because the company has since switched texts off or the
   * number no longer parses. Absent means "not answered here", which is what
   * actually happened.
   *
   * `false` is only sent when it means something: an unticked box for the very
   * number the stored consent covers is a withdrawal. An unticked box for a
   * number that was just changed is not a withdrawal at all — the old consent
   * simply no longer applies — so it is left out and the stored record keeps
   * saying what it always said.
   */
  function payload(): Record<string, unknown> {
    const { smsOptIn, smsMarketingEnabled, emailMarketingEnabled, defaultTeamId, teamFlexible, ...base } = form;
    const rest = {
      ...base,
      ...(defaultTeamId !== (savedCustomer?.defaultTeamId ?? null) ? { defaultTeamId } : {}),
      ...(teamFlexible !== (savedCustomer?.teamFlexible ?? false) ? { teamFlexible } : {}),
    };
    const initialMarketing = initialMarketingRef.current;
    // PATCH only carries an explicit marketing change. In particular, a
    // legacy/offline Customer without these newer keys stays "unknown" here:
    // treating absence as false would revoke real consent on an unrelated edit
    // after the browser reconnects.
    const marketingChanges: Record<string, boolean> = {};
    if (smsMarketingEnabled !== undefined && smsMarketingEnabled !== initialMarketing.smsMarketingEnabled) {
      marketingChanges.smsMarketingEnabled = smsMarketingEnabled;
    }
    if (emailMarketingEnabled !== undefined && emailMarketingEnabled !== initialMarketing.emailMarketingEnabled) {
      marketingChanges.emailMarketingEnabled = emailMarketingEnabled;
    }
    const body = { ...rest, ...marketingChanges };
    if (!smsOffered || !textable) return body;
    if (!smsOptIn && !consentCoversTypedNumber) return body;
    return { ...body, smsOptIn };
  }

  // What the property would be built from, so the box is only offered when
  // there is an address to copy. The same street-address rule the API applies.
  const billingStreetEntered = form.billingAddressLine1.trim() !== '';
  const savingBillingProperty = !customer && addBillingProperty && billingStreetEntered;

  // "This person may already exist" — asked as contact details are typed, so a
  // household calling back does not become a second record whose quotes and
  // schedules nobody can find later. Debounced by state: the probe values only
  // move once typing pauses, and the query key only changes with them. Create
  // mode only; editing an existing customer is by definition not a duplicate.
  const [dupProbe, setDupProbe] = useState<{ email: string; phone: string }>({
    email: '',
    phone: '',
  });
  useEffect(() => {
    if (customer) return;
    const timer = setTimeout(() => setDupProbe({ email: form.email.trim(), phone: form.phone.trim() }), 500);
    return () => clearTimeout(timer);
  }, [customer, form.email, form.phone]);

  const duplicates = useQuery({
    queryKey: ['customer-duplicates', dupProbe],
    queryFn: () =>
      customerApi.checkDuplicates({
        ...(dupProbe.email.includes('@') ? { email: dupProbe.email } : {}),
        ...(dupProbe.phone.length >= 7 ? { phone: dupProbe.phone } : {}),
      }),
    enabled: !customer && (dupProbe.email.includes('@') || dupProbe.phone.length >= 7),
  });
  const duplicateMatches = !customer ? (duplicates.data?.matches ?? []) : [];

  const mutation = useMutation({
    mutationFn: () =>
      customer
        ? customerApi.update(customer.id, payload())
        : customerApi.create({
            ...payload(),
            addBillingAddressAsProperty: savingBillingProperty,
          }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (savingBillingProperty) {
        void queryClient.invalidateQueries({
          queryKey: ['customer-properties', saved.id],
        });
      }
      onSaved(saved);
    },
    onError: (caught) => {
      if (caught instanceof ApiRequestError) {
        setError(caught.fields && Object.keys(caught.fields).length > 0 ? null : caught.message);
        setFieldErrors(caught.fields);
        if (Object.keys(caught.fields).length > 0) setMoreDetails(true);
      } else {
        setError(t('customerForm.saveError'));
      }
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (mutation.isPending) return;
    setError(null);
    setFieldErrors({});
    mutation.mutate();
  }

  return (
    <form className="customer-work-form" ref={formRef} onSubmit={handleSubmit} noValidate>
      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      <fieldset className="customer-work-panel">
        <legend>{i18n.language.startsWith('es') ? 'Datos de contacto' : 'Contact details'}</legend>
        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextInput
            label={t('customerForm.firstName')}
            value={form.firstName}
            error={fieldErrors.firstName}
            onChange={set('firstName')}
          />
          <TextInput
            label={t('customerForm.lastName')}
            value={form.lastName}
            error={fieldErrors.lastName}
            onChange={set('lastName')}
          />
        </div>

        <TextInput
          label={t('customerForm.companyName')}
          hint={t('customerForm.companyNameHint')}
          value={form.companyName}
          error={fieldErrors.companyName}
          onChange={set('companyName')}
        />

        <Select
          label={t('customerForm.serviceSegment')}
          hint={t('customerForm.serviceSegmentHint')}
          value={form.serviceSegment}
          error={fieldErrors.serviceSegment}
          onChange={set('serviceSegment')}
          options={CUSTOMER_SERVICE_SEGMENTS.map((segment) => ({
            value: segment,
            label: t(`customerForm.serviceSegmentOption.${segment}`),
          }))}
        />

        <div className="grid gap-x-4 sm:grid-cols-2">
          <TextInput
            label={t('customerForm.email')}
            type="email"
            value={form.email}
            error={fieldErrors.email}
            onChange={set('email')}
          />
          <TextInput
            label={t('customerForm.phone')}
            type="tel"
            value={form.phone}
            error={fieldErrors.phone}
            onChange={set('phone')}
          />
        </div>

        {duplicateMatches.length > 0 ? (
          <div className="mb-4">
            <Alert tone="warning" title={t('customerForm.duplicateTitle')}>
              <p>{t('customerForm.duplicateBody')}</p>
              <ul className="mt-1 list-inside list-disc">
                {duplicateMatches.map((match) => (
                  <li key={match.id}>
                    {/* A look-aside, not a departure. This form is embedded
                      mid-flow — inside the quote builder and the booking form
                      — so following the link in place would unmount the page
                      holding the rule, the crew and the price, which is the
                      loss those inline forms exist to prevent. */}
                    <Link
                      to={`/customers/${match.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline"
                    >
                      {match.displayName}
                    </Link>{' '}
                    <span className="text-[color:var(--color-muted)]">
                      {[match.email, match.phone].filter(Boolean).join(' · ')}
                    </span>
                  </li>
                ))}
              </ul>
            </Alert>
          </div>
        ) : null}
      </fieldset>
      <fieldset className="customer-work-panel">
        <legend className="px-1 text-sm font-semibold">
          {t(
            !customer && addBillingProperty
              ? 'customerForm.cleaningAddressLegend'
              : 'customerForm.billingAddressLegend',
          )}
        </legend>
        {!customer && addBillingProperty ? (
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('customerForm.cleaningAddressHint')}</p>
        ) : null}
        <TextInput
          label={t('customerForm.addressLine1')}
          value={form.billingAddressLine1}
          error={fieldErrors.billingAddressLine1}
          onChange={set('billingAddressLine1')}
        />
        <TextInput
          label={t('customerForm.addressLine2')}
          value={form.billingAddressLine2}
          error={fieldErrors.billingAddressLine2}
          onChange={set('billingAddressLine2')}
        />
        <div className="grid gap-x-4 sm:grid-cols-3">
          <TextInput
            label={t('customerForm.city')}
            error={fieldErrors.billingCity}
            value={form.billingCity}
            onChange={set('billingCity')}
          />
          <TextInput
            label={t('customerForm.state')}
            error={fieldErrors.billingState}
            value={form.billingState}
            onChange={set('billingState')}
          />
          <TextInput
            label={t('customerForm.postalCode')}
            value={form.billingPostalCode}
            error={fieldErrors.billingPostalCode}
            onChange={set('billingPostalCode')}
          />
        </div>
      </fieldset>

      <details
        className="customer-work-panel"
        open={moreDetails}
        onToggle={(event) => setMoreDetails(event.currentTarget.open)}
      >
        <summary className="cursor-pointer py-3 font-semibold text-[color:var(--color-brand)]">
          {t('customerForm.moreDetails')}
        </summary>
        <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('customerForm.moreDetailsHint')}</p>
        {!customer ? (
          <Checkbox
            label={t('customerForm.billingIsCleanedAddress')}
            hint={
              billingStreetEntered
                ? t('customerForm.billingIsCleanedAddressHint')
                : t('customerForm.billingIsCleanedAddressHintNoAddress')
            }
            checked={savingBillingProperty}
            disabled={!billingStreetEntered}
            onChange={(event) => setAddBillingProperty(event.target.checked)}
          />
        ) : null}
        {teamPreferences ? (
          <>
            <Select
              label={t('savedTeams.recurringTeam')}
              value={form.defaultTeamId ?? ''}
              onChange={(event) => setForm((current) => ({ ...current, defaultTeamId: event.target.value || null }))}
              options={[
                { value: '', label: t('savedTeams.noTeam') },
                ...(savedTeams.data?.items ?? [])
                  .filter((entry) => entry.isActive || entry.id === form.defaultTeamId)
                  .map((entry) => ({ value: entry.id, label: entry.name })),
              ]}
            />
            <Checkbox
              label={t('savedTeams.flexible')}
              checked={form.teamFlexible}
              onChange={(event) => setForm((current) => ({ ...current, teamFlexible: event.target.checked }))}
            />
          </>
        ) : null}
        {/* Earlier-visit offers are on every paid plan, never Free: no switch
            for a customer to be signed up to something that cannot happen. */}
        {offersEarlierVisits ? (
          <Checkbox
            label={t('customerForm.earlierTimes')}
            hint={t(smsOffered ? 'customerForm.earlierTimesHint' : 'customerForm.earlierTimesHintEmail')}
            checked={form.wantsEarlierTimes}
            onChange={(event) => setForm((current) => ({ ...current, wantsEarlierTimes: event.target.checked }))}
          />
        ) : null}

        {smsOffered ? (
          <Checkbox
            label={t('customerForm.smsOptIn', { companyName: company?.name?.trim() || 'BlastoClean' })}
            hint={
              !textable
                ? t('customerForm.smsOptInHintNoNumber')
                : // Only meaningful while the typed number is still the one the
                  // carrier was asked about; an edit in progress has no answer.
                  landlineOnFile
                  ? t('customerForm.smsOptInHintLandline')
                  : consentedNumberRef.current && !consentCoversTypedNumber
                    ? t('customerForm.smsOptInHintDifferentNumber')
                    : t('customerForm.smsOptInHintDefault')
            }
            checked={form.smsOptIn && textable}
            disabled={!textable}
            onChange={(event) => {
              const checked = event.target.checked;
              draftSmsConsentPhoneRef.current = checked ? typedNumber : null;
              if (!checked) smsMarketingPhoneRef.current = null;
              setForm((current) => ({
                ...current,
                smsOptIn: checked,
                ...(checked ? {} : { smsMarketingEnabled: false }),
              }));
            }}
          />
        ) : null}

        {customer && customer.smsConsentStatus !== 'unknown' ? (
          <p className="field-hint -mt-2 mb-4">
            {t('customerForm.smsConsentSummary', {
              status: t(`customers.smsConsent.${customer.smsConsentStatus}`).toLowerCase(),
            })}
            {customer.smsConsentAt
              ? t('customerForm.smsConsentOn', {
                  date: new Intl.DateTimeFormat(getDateLocale()).format(new Date(customer.smsConsentAt)),
                })
              : ''}
            {customer.smsConsentQuoteForm ? t('customerForm.smsConsentQuoteForm') : ''}.
          </p>
        ) : null}

        <fieldset className="mb-4 rounded-md border border-[color:var(--color-line)] p-4">
          <legend className="px-1 text-sm font-semibold">{t('customerForm.marketingLegend')}</legend>
          <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('customerForm.marketingDescription')}</p>
          <Checkbox
            label={t('customerForm.emailMarketingEnabled')}
            hint={emailReachable ? t('customerForm.emailMarketingHint') : t('customerForm.emailMarketingHintNoEmail')}
            checked={form.emailMarketingEnabled === true}
            disabled={!emailReachable}
            onChange={(event) => {
              const checked = event.target.checked;
              emailMarketingAddressRef.current = checked ? normalizedEmail : null;
              setForm((current) => ({
                ...current,
                emailMarketingEnabled: checked,
              }));
            }}
          />
          <Checkbox
            label={t('customerForm.smsMarketingEnabled')}
            hint={
              !textable
                ? t('customerForm.smsMarketingHintNoNumber')
                : !form.smsOptIn
                  ? // Name the box to tick, and only when it is actually on
                    // the page. With texting off there is nothing above, and
                    // with texting never set up Settings has no switch either.
                    t(
                      smsOffered
                        ? 'customerForm.smsMarketingHintNeedsConsent'
                        : smsSendable
                          ? 'customerForm.smsMarketingHintTextingOff'
                          : 'customerForm.smsMarketingHintNotSetUp',
                    )
                  : t('customerForm.smsMarketingHint')
            }
            checked={form.smsMarketingEnabled === true}
            disabled={!textable || !form.smsOptIn}
            onChange={(event) => {
              const checked = event.target.checked;
              smsMarketingPhoneRef.current = checked ? typedNumber : null;
              setForm((current) => ({
                ...current,
                smsMarketingEnabled: checked,
              }));
            }}
          />
        </fieldset>

        {/*
        How this customer is billed: net terms (snapshotted onto each invoice
        at issue), their own PO number, and whether the month's visits are
        rolled into one invoice.

        All three are commercial questions — Net 30 against a purchase order,
        one document a month for a client with ten buildings — and a company
        that only cleans houses was being asked them before it was asked
        anything a housecleaner would recognize. The defaults are the
        residential answers, so hiding the block changes no stored value: the
        form submits whatever the record already carried, untouched.

        Shown anyway where a value is NOT the default, which is the company
        that ran commercial work and switched back. Their Net-30 client is
        still Net 30, and a field that silently could not be edited would be
        worse than one nobody needed.
      */}
        {commercialWork || carriesBillingTerms ? (
          <fieldset className="mb-4 rounded-md border border-[color:var(--color-line)] p-4">
            <legend className="px-1 text-sm font-semibold">{t('customerForm.billingLegend')}</legend>
            <div className="grid gap-x-4 sm:grid-cols-2">
              <Select
                label={t('customerForm.paymentTermsLabel')}
                hint={t('customerForm.paymentTermsHint')}
                value={form.invoiceTermsDays === null ? '' : String(form.invoiceTermsDays)}
                error={fieldErrors.invoiceTermsDays}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    invoiceTermsDays: event.target.value === '' ? null : Number(event.target.value),
                  }))
                }
                options={[
                  {
                    value: '',
                    label: t('customerForm.paymentTermsDueOnReceipt'),
                  },
                  // A stored value the list does not offer (set through the API)
                  // still has to show as itself, not silently as the first option.
                  ...(form.invoiceTermsDays !== null && !NET_TERMS_CHOICES.includes(form.invoiceTermsDays)
                    ? [
                        {
                          value: String(form.invoiceTermsDays),
                          label: t('customerForm.paymentTermsNet', {
                            days: form.invoiceTermsDays,
                          }),
                        },
                      ]
                    : []),
                  ...NET_TERMS_CHOICES.map((days) => ({
                    value: String(days),
                    label: t('customerForm.paymentTermsNet', { days }),
                  })),
                ]}
              />
              <TextInput
                label={t('customerForm.poNumberLabel')}
                hint={t('customerForm.poNumberHint')}
                maxLength={80}
                value={form.invoicePoNumber}
                error={fieldErrors.invoicePoNumber}
                onChange={set('invoicePoNumber')}
              />
            </div>
            <Checkbox
              label={t('customerForm.consolidatedBillingLabel')}
              hint={t('customerForm.consolidatedBillingHint')}
              checked={form.consolidatedBilling}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  consolidatedBilling: event.target.checked,
                }))
              }
            />
          </fieldset>
        ) : null}

        <TextArea
          label={t('customerForm.internalNotes')}
          hint={t('customerForm.internalNotesHint')}
          tone="internal"
          rows={3}
          error={fieldErrors.internalNotes}
          value={form.internalNotes}
          onChange={set('internalNotes')}
        />
      </details>

      <div className="flex gap-3">
        <Button type="submit" variant="primary" busy={mutation.isPending}>
          {customer ? t('customerForm.saveChanges') : t('customerForm.addCustomer')}
        </Button>
        {onCancel ? (
          <Button disabled={mutation.isPending} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}
